import { ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core'
import { Facet, FacetCount } from 'smartsearch.js'
import { triggerSmoothCollapseAnimation } from '../../../../../shared/animations/smooth-collapse.animation'
import { ActivatedRoute } from '@angular/router'
import { Subscription } from 'rxjs'
import { CmsFacetService } from '../../../../services/cms-search/cms-facet.service'
import { take } from 'rxjs/operators'
import { CmsFacet, CmsFacetCount, ContentQuery } from '../../../types'
import { MbSpinnerService } from '../../../../../shared/services/mb-spinner.service'
import { CmsLinkType } from '../../../../enums/cms-link-type'

@Component({
  selector: 'mb-cms-facet',
  templateUrl: './cms-facet.component.html',
  styleUrls: ['./cms-facet.component.scss'],
  animations: [triggerSmoothCollapseAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsFacetComponent implements OnInit, OnDestroy {

  readonly showMoreThreshold = 5

  @Input() facetCategory!: Facet
  @Input() currentFilter = ''
  @Input() jobs?: boolean

  isExpanded = false
  showMore = false
  isMoreExpanded = false
  subscription = new Subscription()
  query: string | undefined
  queryContent: string | undefined

  @HostBinding('class') toggleClass: string = ''

  constructor (private route: ActivatedRoute,
               private cmsFacetService: CmsFacetService,
               private mbSpinnerService: MbSpinnerService,
               private vcr: ViewContainerRef) {
  }

  ngOnInit (): void {
    this.initState()

    this.subscription.add(this.route.queryParamMap.subscribe(params => {
      this.query = params.get('query') || undefined
      this.queryContent = params.get('queryContent') || undefined
    }))

    this.subscription.add(this.cmsFacetService.readonlyFacets$.pipe(take(1))
      .subscribe((readonlyFacets = []) => {
        if (readonlyFacets.some(readonlyFacet => this.getCmsFacetCategoryName(readonlyFacet) === this.getFacetCategoryName())) {
          this.showMore = this.facetCategory?.counts?.length > this.showMoreThreshold || false
        }
      })
    )
  }

  toggleExpanded (override?: boolean): void {
    this.isExpanded = override ?? !this.isExpanded
    this.toggleClass = this.isExpanded ? 'expanded' : 'collapsed'
  }

  getQueryParam (facetCount: CmsFacetCount): ContentQuery {
    return this.isFacetCountSelected(facetCount)
      ? this.cmsFacetService.getQueryParamWithoutFacetCount(facetCount, this.jobs)
      : this.cmsFacetService.getQueryParamWithFacetCount(facetCount, this.jobs)
  }

  isFacetCountSelected (facetCount: CmsFacetCount): boolean {
    const facet = this.cmsFacetService.getNormalizedUrlFilterQuery(facetCount)
    return (this.currentFilter ?? '').split('&')
      .filter(filter => !!filter)
      .some(filter => filter === facet)
  }

  isReadonlyFacet (facetCount: FacetCount | CmsFacetCount): boolean {
    return this.cmsFacetService.isReadonlyFacet(facetCount)
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
  }

  getFacetCategoryName (): string {
    return this.facetCategory?.config?.baseName?.replaceAll('_', ' ') ?? ''
  }

  getCmsFacetCategoryName (cmsFacet: CmsFacet): string {
    return cmsFacet.category?.replaceAll('_', ' ')
  }

  onFacetClick () {
    this.mbSpinnerService.launchMbSpinner(this.vcr)
  }

  private initState () {
    this.showMore = this.facetCategory?.counts?.length > this.showMoreThreshold || false
    const isExpanded = this.facetCategory?.counts?.some((facet: any) => this.currentFilter.includes(facet?.urlFilterQuery))
    if (isExpanded) {
      this.toggleExpanded(true)
    }
    if (this.facetCategory?.counts?.length > this.showMoreThreshold) {
      this.isMoreExpanded = this.facetCategory.counts.slice(this.showMoreThreshold)
        .some((facet: any) => this.currentFilter.includes(facet?.urlFilterQuery))
    }
  }

  protected readonly cmsLinkType = CmsLinkType
}
